/**
 * Which side of the counter the Parts shell shows (spec §4.1): the service
 * workspace under `/parts`, or the wholesale one under `/wholesale`. The
 * URL carries the side; the browser remembers the last one opened so a
 * Parts login, the root address and the sign-in redirect reopen it.
 *
 * The memory is `localStorage` — per browser, never per account: it is a
 * convenience for the one desk that works both sides, not data. Storage may
 * be missing or refused (a private window, a policy), in which case the
 * side simply is not remembered and every read says Service.
 *
 * Each side also keeps its own *place* (spec §4.1: "selected list, open
 * order"): the last address shown on that side, in `sessionStorage` — per
 * tab, so two tabs pinned to the two sides never trade places — written
 * by the shell on every location change and read by the switch, which
 * returns to it rather than to the side's home list.
 */

export type PartsSide = "service" | "wholesale";

export const PARTS_SIDE_STORAGE_KEY = "parts.side";

export const DEFAULT_PARTS_SIDE: PartsSide = "service";

export function isPartsSide(value: unknown): value is PartsSide {
  return value === "service" || value === "wholesale";
}

/** The side the browser last showed, or Service when nothing (or rubbish) is stored. */
export function readRememberedSide(): PartsSide {
  try {
    const stored = window.localStorage.getItem(PARTS_SIDE_STORAGE_KEY);
    return isPartsSide(stored) ? stored : DEFAULT_PARTS_SIDE;
  } catch {
    return DEFAULT_PARTS_SIDE;
  }
}

export function rememberSide(side: PartsSide): void {
  try {
    window.localStorage.setItem(PARTS_SIDE_STORAGE_KEY, side);
  } catch {
    // Storage refused: the side is simply not remembered.
  }
}

/** Where each side starts: the service Work queue, or the wholesale Open list. */
export function sideHomePath(side: PartsSide): string {
  return side === "wholesale" ? "/wholesale?view=open" : "/parts?view=work_queue";
}

/** The side a path belongs to (`/wholesale…` is wholesale; everything else Service). */
export function sideOfPath(pathname: string): PartsSide {
  return pathname === "/wholesale" || pathname.startsWith("/wholesale/") ? "wholesale" : "service";
}

// ---------------------------------------------------------------------------
// Each side's place
// ---------------------------------------------------------------------------

export function placeStorageKey(side: PartsSide): string {
  return `parts.place.${side}`;
}

/**
 * True for an address the switch may link to as `side`'s place: a path on
 * this origin (`/…`, never `//host` or a URL) that belongs to that side.
 * Anything else in storage — rubbish, the other side's address — is
 * ignored rather than followed.
 */
export function isPlaceOn(side: PartsSide, place: string): boolean {
  if (!place.startsWith("/") || place.startsWith("//")) return false;
  const pathname = place.split(/[?#]/, 1)[0] ?? "";
  return sideOfPath(pathname) === side;
}

/** Records `place` (pathname + search) as where `side` was last shown in this tab. */
export function rememberPlace(side: PartsSide, place: string): void {
  if (!isPlaceOn(side, place)) return;
  try {
    window.sessionStorage.setItem(placeStorageKey(side), place);
  } catch {
    // Storage refused: the place is simply not remembered.
  }
}

/** Where `side` was last shown in this tab, or `null` when nothing usable is stored. */
export function readRememberedPlace(side: PartsSide): string | null {
  try {
    const stored = window.sessionStorage.getItem(placeStorageKey(side));
    return stored !== null && isPlaceOn(side, stored) ? stored : null;
  } catch {
    return null;
  }
}

/** Where the switch sends someone choosing `side`: its remembered place, else its home. */
export function sidePlacePath(side: PartsSide): string {
  return readRememberedPlace(side) ?? sideHomePath(side);
}
